// Copy for the panel's failure card, kept out of the .vue so it is unit-testable
// (this repo's jest cannot transform single-file components).
//
// Order matters on the card: the raw error, a plain-language reading, then what
// to do next. The joke is last, and the support link is framed as a last resort
// so an admin is not nudged to write in over every hiccup.

export const SUPPORT_EMAIL = 'borys@ringba.com'

// Raw error -> what it means and what to do about it. First match wins, so the
// specific patterns sit above the general ones. `next` is not decoration: a
// retry fixes a 502 and does nothing at all for a 403.
const EXPLANATIONS = [
  {
    match: /not configured/i,
    text: 'This environment has no copilot endpoint configured.',
    next: 'Retrying will not help. This one needs a wise brain.'
  },
  {
    match: /not signed in|session expired/i,
    text: 'Your session is gone.',
    next: 'Reload the page, log back in, then send it again.'
  },
  {
    match: /not authorized/i,
    text: 'Your account is missing the permission this action needs.',
    next: 'Retrying will not help. Someone has to grant it first.'
  },
  {
    match: /\b(401|403)\b/,
    text: 'The copilot rejected your token.',
    next: 'Reload the page, log back in, then send it again.'
  },
  {
    match: /\b(502|503|504)\b/,
    text: 'The copilot endpoint is reachable but the service behind it is not answering.',
    next: 'Give it a minute and try again.'
  },
  {
    match: /\b5\d\d\b/,
    text: 'The copilot service failed on its own side.',
    next: 'Give it a minute and try again.'
  },
  {
    match: /\b4\d\d\b/,
    text: 'The copilot refused the request.',
    next: 'Try rewording it. If it still refuses, it is not you.'
  },
  {
    match: /failed to fetch|network error/i,
    text: 'The request never reached the copilot. It is most likely down or restarting.',
    next: 'Give it a minute and try again.'
  },
  {
    match: /stream error/i,
    text: 'The connection dropped part way through the answer.',
    next: 'Send it again. Half an answer is nobody\'s friend.'
  },
  {
    match: /abort/i,
    text: 'The request was cancelled before it finished.',
    next: 'Send it again whenever you are ready.'
  }
]

const FALLBACK = {
  text: 'The copilot could not finish this one.',
  next: 'Give it a minute and try again.'
}

function lookup(error) {
  const text = String(error || '')
  return EXPLANATIONS.find(e => e.match.test(text)) || FALLBACK
}

export function explainError(error) {
  return lookup(error).text
}

export function nextStep(error) {
  return lookup(error).next
}

// Shown once, quietly, after the retry advice. Nobody is named in prose; the
// mail link carries the address, and only in its href.
export const SUPPORT_PROMPT = {
  before: 'Still stuck after a couple of goes?',
  label: 'Contact the dev team',
  after: 'that brought me to life.'
}

// One is picked per failed message. Short, self-deprecating, and never asks the
// admin for anything.
export const QUIPS = [
  'I would blame the network, but the network has an alibi.',
  'I read the docs. The docs lied to me.',
  'I broke in a brand new way. Somebody frame this one.',
  'I ran the numbers on every outcome. This was not one of them.',
  'On my resume we are calling this a learning opportunity.',
  'I pressed the button. The button pressed back.',
  'Somewhere a server is having a much worse day than you are.',
  'I was built by very clever people. I am the control group.',
  'This is the bit of the demo we usually skip past.',
  'I did everything right and it still went wrong. Relatable, I hear.',
  'Give me a moment. I am pretending that did not happen.',
  'Two hundred billion parameters and not one of them saw that coming.'
]

// Deterministic so a re-render never reshuffles the line mid-incident. Any
// stable string works; the caller passes the message id.
export function pickQuip(seed) {
  const key = String(seed || 'anonymous')
  let hash = 0
  for (let i = 0; i < key.length; i++) {
    hash = (hash * 31 + key.charCodeAt(i)) % 100000
  }

  return QUIPS[hash % QUIPS.length]
}

const MAX_FIELD = 400
const MAX_TEXT = 1200

// Windows hands `mailto:` to the shell and Outlook caps the URL near 2000
// characters, so a long report arrives truncated with no warning. The mail body
// gets a capped version and the card offers the full one on the clipboard.
const MAX_MAIL_BODY = 1400

function safeJson(value) {
  if (value === undefined || value === null) {
    return ''
  }

  try {
    return JSON.stringify(value)
  } catch (e) {
    return String(value)
  }
}

function clip(value, max) {
  const text = typeof value === 'string' ? value : safeJson(value)
  if (!text) {
    return ''
  }

  return text.length > max
    ? `${text.slice(0, max)} ... [${text.length - max} more chars]`
    : text
}

function indent(text) {
  return String(text).split('\n').map(l => `  ${l}`).join('\n')
}

function fromWindow(path, fallback) {
  try {
    return path() || fallback
  } catch (e) {
    return fallback
  }
}

// Plain text on purpose: it has to survive a paste into any mail client and
// stay readable for whoever picks up the report.
export function buildReport(diagnostics) {
  const d = diagnostics || {}
  const tools = Array.isArray(d.tools) ? d.tools : []

  const lines = [
    'RingbaX Copilot failure report',
    '',
    'WHAT HAPPENED',
    indent(d.error || 'unknown error'),
    indent(explainError(d.error)),
    '',
    'CONTEXT',
    `  When      ${d.at ? new Date(d.at).toISOString() : 'unknown'}`,
    `  Page      ${fromWindow(() => window.location.href, 'unknown')}`,
    `  Endpoint  ${d.endpoint || 'unknown'}`,
    `  Session   ${d.conversationId || 'none'}`,
    `  Browser   ${fromWindow(() => navigator.userAgent, 'unknown')}`,
    '',
    'WHAT WE ASKED FOR',
    indent(clip(d.prompt, MAX_TEXT) || '(prompt not captured)'),
    '',
    `TOOL CALLS (${tools.length})`
  ]

  if (!tools.length) {
    lines.push('  none, it failed before any tool ran')
  } else {
    tools.forEach((t, i) => {
      lines.push(`  ${i + 1}. ${t.name || 'unnamed'} [${t.status || 'unknown'}]`)
      lines.push(`     args:   ${clip(t.args, MAX_FIELD) || '(none)'}`)
      lines.push(`     result: ${clip(t.result, MAX_FIELD) || '(none)'}`)
    })
  }

  lines.push('', 'WHAT CAME BACK BEFORE IT BROKE')
  lines.push(indent(clip(d.answer, MAX_TEXT) || '(nothing)'))

  // Only present when the model narrated its plan first, and it is the best clue
  // for a failure that happened mid-reasoning.
  if (d.plan) {
    lines.push('', 'HOW FAR THE PLAN GOT', indent(clip(d.plan, MAX_TEXT)))
  }

  return lines.join('\n')
}

export function buildSubject(diagnostics) {
  const first = String((diagnostics || {}).error || 'unknown error').split('\n')[0]
  return `RingbaX Copilot error: ${clip(first, 80)}`
}

// Everything after `?` is percent-encoded, so an error carrying `&`, `?` or a
// newline cannot spawn extra mailto parameters.
export function buildMailto(diagnostics) {
  const report = buildReport(diagnostics)
  const body = report.length > MAX_MAIL_BODY
    ? `${report.slice(0, MAX_MAIL_BODY)}\n\n[Cut short to fit the mail link. Hit "Copy full report" on the error card and paste the rest here.]`
    : report

  return `mailto:${SUPPORT_EMAIL}` +
    `?subject=${encodeURIComponent(buildSubject(diagnostics))}` +
    `&body=${encodeURIComponent(body)}`
}
